/**
 * ============================================================================
 * ⭐⭐ SWITCHING BETWEEN THE SCHEDULER AND PIPELINE (Decision 234, 2026-09-28)
 * ============================================================================
 *
 * > Pete, 2026-09-28: *"is it easy to wire up pipeline on the menu? It would be
 * > nice to not have two open browsers and switch back and forth from Scheduler
 * > to Pipeline"* → popup: *"Switch between the two tabs (Recommended)"*.
 *
 * ---- ⛔⤴ ~~TWO NAMED TABS~~ — REVERSED THE SAME DAY (Decision 243) ---------
 *
 * > Pete, on the live site: *"When I click on pipeline, it opens a second tab???
 * > Thats not what we said"* → popup *"One tab: this page becomes Pipeline"*, and
 * > in his words: *"the same browser tab, scheduler turns into Pipeline on the
 * > screen right?"*
 *
 * ⭐ ONE BROWSER TAB. The Pipeline pill sends THIS tab to Pipeline; Pipeline's
 * Scheduler button sends it back. Never a second tab, and nothing to find by
 * name, so a Microsoft sign-in in between cannot break it.
 * ⚠️ THE COST, STATED: each switch loads the other app afresh — the Scheduler
 * comes back on today's board, not the screen that was left. Pete chose that.
 * ⛔ `location.assign`, not `replace`: Back returns to the app you came from.
 *
 * ⛔ STILL NOT A FRAME. Microsoft sign-in refuses to run inside one, and this app
 * sends `X-Frame-Options: DENY` itself.
 *
 * ---- ⛔ THE ADDRESS IS CONFIGURATION, NEVER A LITERAL -------------------------
 *
 * `VITE_PIPELINE_URL`, set at build (`.env.local` on the deploying workstation,
 * documented in `.env.example`). Unset or not https → `pipelineUrl()` is null and
 * the Pipeline view says exactly which setting is missing.
 */

/** The Pipeline app's address, or null when the build was not given one. */
export function pipelineUrl(raw: string | undefined = import.meta.env.VITE_PIPELINE_URL): string | null {
    const v = (raw ?? '').trim();
    if (!v) return null;
    try {
        const u = new URL(v);
        /* ⛔ https only. An http or relative value is a mistake in the setting,
           and silently following it is how a tab ends up somewhere nobody meant. */
        return u.protocol === 'https:' ? u.toString() : null;
    } catch {
        return null;
    }
}

/** Send THIS browser tab to the other app (Decision 243). */
export function goToApp(url: string, loc: Pick<Location, 'assign'> = window.location): void {
    loc.assign(url);
}

/**
 * ============================================================================
 * ⭐ F154 — THE WAREHOUSE TAB (Decision 330, 2026-09-29)
 * ============================================================================
 *
 * > Pete: *"just like we did for pipeline, we need to add a link for
 * > https://dockscheduler.symbia.com/board?cfg=main but can it be next to Calendar
 * > and Reporting, on that line, not a new app at the top? Tab should be called
 * > Warehouse"* → popup: the same browser tab (*"Number 2 - but it is already set up
 * > this way as the reporting tab right?"*).
 *
 * ⭐ THE SAME BROWSER TAB, like the Pipeline switch: `goToApp` (location.assign), so
 * Back returns to the Scheduler.
 * ⛔ A RELATIVE PATH, NEVER A HOST. The board is this app's own `/board` page, so the
 * link stays on whatever address the Scheduler was opened from — nothing here names
 * dockscheduler.symbia.com, and a local build can never point at production.
 * ⭐ `main` is the saved board config Pete named (dock.BoardConfig, migration 136).
 * ⚠️ The board is IP-gated (kiosk/board): off a registered facility network it says
 * so rather than showing trucks. That is the board's rule, not this tab's.
 */
// ⭐ F161 (Decision 334): the Warehouse TAB now draws the board inside the Scheduler
//   (App.tsx PageRouter); `warehouseBoardPath` stays for a link to the full-screen board.
export const WAREHOUSE_BOARD_CONFIG = 'main';

/** `/board?cfg=main` — relative, so it follows the address the Scheduler is on. */
export function warehouseBoardPath(cfg: string = WAREHOUSE_BOARD_CONFIG): string {
    return `/board?cfg=${encodeURIComponent(cfg)}`;
}

/**
 * ============================================================================
 * ⭐ F172 — PIPELINE'S LABOR AND CLIENT TABS OPEN THE SCHEDULER ON THAT APP
 * ============================================================================
 *
 * > Pete, 2026-09-29: *"pipeline header and dock scheduler header should be the same
 * > in regards to app toolbar next to the symbia logo"*.
 *
 * Pipeline now shows the same four apps (Scheduler · Labor · Client · Pipeline).
 * Labor and Client live here, so Pipeline sends this tab to the Scheduler with
 * `?app=labor` / `?app=client`; App.tsx opens on that app and then removes the
 * parameter from the address bar (a reload lands on the Scheduler as usual).
 * ⛔ `pipeline` is NOT linkable: it would send the tab straight back to Pipeline.
 * ⛔ Anything else is ignored (the Scheduler opens as before).
 */
export const APP_QUERY_PARAM = 'app';
/* ⭐ F218: `client` is no longer linkable — the Client pill opens the client portal in
   a new browser tab (see PORTAL_APPS), so there is no Client app inside the Scheduler
   to land on. An old `?app=client` opens the Scheduler, like any unknown value. */
export const LINKABLE_APPS = ['scheduler', 'labor'] as const;
export type LinkableApp = (typeof LINKABLE_APPS)[number];

/** The app a `?app=` asks for, or null when absent or not one of LINKABLE_APPS. */
export function appFromSearch(search: string): LinkableApp | null {
    let v: string | null = null;
    try { v = new URLSearchParams(search).get(APP_QUERY_PARAM); } catch { return null; }
    const t = (v ?? '').trim().toLowerCase();
    return (LINKABLE_APPS as readonly string[]).includes(t) ? (t as LinkableApp) : null;
}

/** The same address without `?app=`, or null when it carries none (nothing to rewrite). */
export function withoutAppParam(href: string): string | null {
    try {
        const u = new URL(href);
        if (!u.searchParams.has(APP_QUERY_PARAM)) return null;
        u.searchParams.delete(APP_QUERY_PARAM);
        return u.toString();
    } catch {
        return null;
    }
}

/**
 * ============================================================================
 * ⭐ F218 — CLIENT AND CARRIER OPEN THEIR PORTAL IN A NEW BROWSER TAB
 * ============================================================================
 *
 * > Pete, 2026-10-01: *"Lets put Client and Carrier on this tab (client already is) and
 * > for now, have it open a new tab in the browser to the client or carrier portal."*
 *
 * ⛔⤴ ~~a new browser tab~~ — F226, the same afternoon. Pete: *"can we make the client and
 * carrier links open inline the app just like Pipeline and Scheduler do? So it doesn't leave
 * the browser tab"*. Like Decision 243: THIS tab goes to the portal (location.assign, so Back
 * returns to the app you came from).
 * ⛔ A RELATIVE PATH, NEVER A HOST: the portals are this app's own `/client` and `/carrier`
 * pages, so the tab follows whatever address the Scheduler is on.
 * ⭐ The LOGIN page: with a portal session already in this browser it goes straight on to
 * that portal's dashboard (CarrierLogin), otherwise it asks for an email.
 */
export const PORTAL_APPS = {
    client: '/client/login',
    carrier: '/carrier/login',
} as const;
export type PortalApp = keyof typeof PORTAL_APPS;

/** Send THIS tab to a portal (F218, same tab since F226). Only PORTAL_APPS paths reach here. */
export function openPortal(app: PortalApp, loc: Pick<Location, 'assign'> = window.location): void {
    loc.assign(PORTAL_APPS[app]);
}
